import { isAppBuild } from "@/lib/net";
/**
 * Client portal v2 ("Kasavu Night") flag.
 *
 * ON by default for clients since 2026-09-26 (owner decision). Per browser:
 *   - `?ui=v1` → classic (persists `localStorage['ui:v2:default-on'] = '0'`) — a staff/support URL.
 *     ⛔ OWNER RULE: no client-visible control offers this. There is no "Use the classic
 *     portal" row and no "Switch back" link anywhere in v2 (pinned by account.test.ts).
 *   - `?ui=v2` → v2 again (persists '1')
 * No stored choice (or unreadable storage) means the default, UI_V2_DEFAULT.
 *
 * The key is VERSIONED ('ui:v2:default-on'). Before v2 became the default, clients could
 * press a "Use the classic portal" row that persisted the legacy key `ui:v2` = '0'.
 * Honouring that would pin those clients to classic forever with no control to leave it,
 * so the legacy key is ignored (and removed on read). Only `?ui=v1` can choose classic now.
 *
 * ⚠️ SINCE force-v2 (owner 2026-10-01) `resolveUiV2` below is the LEGACY rule. It runs only when
 * the server kill switch says `uiFlags.clientPortal === 'classic'` (CLIENT_PORTAL_FORCE_V2=off,
 * server/lib/uiFlags.ts). The live rule is `portalFor`: every client-side role (client,
 * collaborator, planner) gets v2 at every width and in the app; `?ui=v1`, the stored '0' and
 * `?view=classic` are ignored for them, and the stored opt-out is removed when seen.
 *
 * Pure functions of (role, url, storage, server flag) so they are safe to call during render and
 * are unit-tested without a DOM (client/src/v2/__tests__/flag.test.ts, metrics.test.ts).
 */
export const UI_V2_STORAGE_KEY = "ui:v2:default-on";
/** Pre-default key. Ignored; removed when seen so it cannot resurface. */
export const UI_V2_LEGACY_STORAGE_KEY = "ui:v2";

/** What a client with no stored choice gets. Flip to false to make classic the default again. */
export const UI_V2_DEFAULT = true;

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function resolveUiV2(
  role: string | null | undefined,
  search: string,
  storage: StorageLike | null,
  /** The mobile app (client-app/) has no classic portal: a client always gets v2 there. */
  app: boolean = isAppBuild(),
): boolean {
  if (app) return role === "client";
  applyLegacyUiParam(search, storage);
  if (role !== "client") return false;
  try {
    const stored = storage?.getItem(UI_V2_STORAGE_KEY);
    if (stored === "1") return true;
    if (stored === "0") return false;
    return UI_V2_DEFAULT;
  } catch {
    return UI_V2_DEFAULT;
  }
}

// ── force-v2 (owner 2026-10-01): "all clients from any device … on the new portal" ──────────

/** Every role the client portal serves (App.tsx CLIENT_PORTAL_ROLES). One list, used everywhere. */
export const CLIENT_SIDE_ROLES: ReadonlySet<string> = new Set(["client", "collaborator", "planner"]);

export function isClientSideRole(role: unknown): boolean {
  return typeof role === "string" && CLIENT_SIDE_ROLES.has(role);
}

/**
 * `user.uiFlags.clientPortal` from GET /api/user/profile. Only an explicit 'classic' is the kill
 * switch; absent (older server, cached profile) or anything else means v2. Never an opt-in.
 */
export type ClientPortalFlag = "v2" | "classic" | null | undefined;

export function serverAllowsClassic(serverFlag: unknown): boolean {
  return serverFlag === "classic";
}

export type Portal = "v2" | "classic" | "none";

export interface PortalInput {
  role: string | null | undefined;
  /** CSS px (isUiV2Enabled passes window.innerWidth). Deliberately unused: no width ever chooses
      classic (owner: desktop, tablet AND phone). The matrix test drives isUiV2Enabled at
      360/768/1024/1440 with that width on the stubbed window, so a width branch added here (or a
      matchMedia read) would fail it. */
  width?: number;
  /** Inside the store app (client-app/). */
  inApp: boolean;
  /** `location.search` (may carry ?ui=v1 / ?view=classic — both ignored while forced). */
  url: string;
  storage: StorageLike | null;
  serverFlag: ClientPortalFlag | string;
}

/**
 * Which client portal this user sees. 'none' = not a client-side role: this function does not
 * decide for staff, vendors, partners… (their branches in App.tsx are unchanged).
 *
 * Forced (server flag not 'classic'): every client-side role → 'v2', whatever the width, the URL
 * or the storage says. Kill switch ('classic'): exactly the pre-force behaviour — `resolveUiV2`
 * (client: default v2, ?ui=v1 honoured; collaborator/planner: classic on the web, refused in the
 * app — see appRoleHome).
 *
 * PURE: never writes storage. `clearClassicOptOut` does the clean-up separately.
 */
export function portalFor(input: PortalInput): Portal {
  if (!isClientSideRole(input.role)) return "none";
  if (!serverAllowsClassic(input.serverFlag)) return "v2";
  if (input.inApp) return input.role === "client" ? "v2" : "none";
  return legacyRead(input.role, input.url, input.storage) ? "v2" : "classic";
}

/** `?view=classic` (V2ClientApp's per-route escape hatch) — honoured only under the kill switch. */
export function honourViewClassic(role: string | null | undefined, serverFlag: unknown): boolean {
  return isClientSideRole(role) && serverAllowsClassic(serverFlag);
}

/** The legacy read WITHOUT the legacy writes (portalFor must stay pure). */
function legacyRead(role: string | null | undefined, search: string, storage: StorageLike | null): boolean {
  if (role !== "client") return false;
  const param = new URLSearchParams(search).get("ui");
  if (param === "v2") return true;
  if (param === "v1") return false;
  try {
    const stored = storage?.getItem(UI_V2_STORAGE_KEY);
    if (stored === "1") return true;
    if (stored === "0") return false;
  } catch {
    /* unreadable: the default */
  }
  return UI_V2_DEFAULT;
}

/** Forced mode: a stored classic choice (from a staff URL, a shared device…) is removed when seen. */
export function clearClassicOptOut(storage: StorageLike | null): void {
  if (!storage) return;
  try {
    if (storage.getItem(UI_V2_STORAGE_KEY) === "0") storage.removeItem(UI_V2_STORAGE_KEY);
    if (storage.getItem(UI_V2_LEGACY_STORAGE_KEY) !== null) storage.removeItem(UI_V2_LEGACY_STORAGE_KEY);
  } catch {
    /* unreadable storage: nothing to clear */
  }
}

/**
 * Kill-switch mode only: the pre-force `?ui=` persistence and the legacy-key migration — the
 * WRITE half of `resolveUiV2`, so that `portalFor`'s pure read sees exactly what it used to.
 */
export function applyLegacyUiParam(search: string, storage: StorageLike | null): void {
  if (!storage) return;
  try {
    if (storage.getItem(UI_V2_LEGACY_STORAGE_KEY) !== null) storage.removeItem(UI_V2_LEGACY_STORAGE_KEY);
  } catch {
    /* unreadable storage: nothing to migrate */
  }
  const param = new URLSearchParams(search).get("ui");
  try {
    if (param === "v2") storage.setItem(UI_V2_STORAGE_KEY, "1");
    else if (param === "v1") storage.setItem(UI_V2_STORAGE_KEY, "0");
  } catch {
    /* private mode / quota */
  }
}

/**
 * The web entry App.tsx calls (after every hook). It DELEGATES the decision to `portalFor` (one
 * rule, one matrix — client/src/v2/__tests__/flag.test.ts runs the full matrix through THIS
 * function with a stubbed window) and only adds the storage side effects:
 *   - forced: the stored classic opt-out is removed when seen;
 *   - kill switch: the legacy `?ui=` persistence (web only, as before).
 * Any other role → false WITHOUT touching storage (a staff ?ui=v1 no longer leaves a '0' behind
 * for the next client on a shared device).
 */
export function isUiV2Enabled(
  role: string | null | undefined,
  serverFlag?: ClientPortalFlag | string,
  inApp: boolean = isAppBuild(),
): boolean {
  if (typeof window === "undefined") return false;
  if (!isClientSideRole(role)) return false;
  let storage: StorageLike | null = null;
  try {
    storage = window.localStorage;
  } catch {
    storage = null;
  }
  const search = window.location?.search ?? "";
  if (!serverAllowsClassic(serverFlag)) clearClassicOptOut(storage);
  else if (!inApp) applyLegacyUiParam(search, storage);
  return portalFor({ role, width: window.innerWidth, inApp, url: search, storage, serverFlag }) === "v2";
}
